import React, { useState, useRef, useEffect } from 'react';
import { getAuthToken } from '../services/api';
import { 
  XMarkIcon, 
  ArrowUpTrayIcon, 
  DocumentMagnifyingGlassIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  PhotoIcon,
  SparklesIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { extractAdmissionFormData, ExtractedAdmissionData } from '../services/geminiOcrService';
import { uploadLeadDocument } from '../services/formStorageService';
import { API_BASE_URL } from '../config/platformConfig';
import { auth } from '@obis/firebase-config';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';
interface BulkImageImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
  branchId: string;
}

interface GroupedFiles {
  studentKey: string;
  formFile: File | null;
  certificateFile: File | null;
  status: 'pending' | 'scanning' | 'ready' | 'error' | 'uploading' | 'completed';
  extractedData?: ExtractedAdmissionData;
  error?: string;
  id?: string; // assigned after scan/create
}

const BulkImageImportModal: React.FC<BulkImageImportModalProps> = ({ 
  isOpen, 
  onClose, 
  onUploadSuccess, 
  branchId 
}) => {
  const [fileGroups, setFileGroups] = useState<GroupedFiles[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setFileGroups([]);
    setIsProcessing(false);
    setProgress(0);
    setError(null);
  };

  const handleFileSelection = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const groups: { [key: string]: GroupedFiles } = {};
    const directDataLeads: GroupedFiles[] = [];

    setIsProcessing(true);

    for (const file of files) {
      const name = file.name.toLowerCase();
      
      if (name.endsWith('.csv') || name.endsWith('.xlsx') || name.endsWith('.xls')) {
        // Handle tabular data locally
        const parseData = await parseTabularFile(file);
        directDataLeads.push(...parseData);
      } else {
        // Image logic
        const isCert = name.includes('birth') || name.includes('certificate') || name.includes('bc') || name.includes('cert');
        const studentKey = name
          .replace(/\.(png|jpe?g|webp|heic)$/i, '')
          .replace(/(birth|certificate|bc|cert)/gi, '')
          .replace(/[^a-z0-9]/gi, ' ')
          .trim() || `unknown_${Math.random().toString(36).substr(2, 5)}`;

        if (!groups[studentKey]) {
          groups[studentKey] = {
            studentKey,
            formFile: null,
            certificateFile: null,
            status: 'pending'
          };
        }

        if (isCert) {
          groups[studentKey].certificateFile = file;
        } else {
          groups[studentKey].formFile = file;
        }
      }
    }

    setFileGroups(prev => [...prev, ...Object.values(groups), ...directDataLeads]);
    setIsProcessing(false);
    // Clear input so same files can be selected again
    if (e.target) e.target.value = '';
  };

  const parseTabularFile = (file: File): Promise<GroupedFiles[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          let jsonData: any[] = [];
          
          if (file.name.toLowerCase().endsWith('.csv')) {
            const csvData = Papa.parse(e.target?.result as string, { header: false, skipEmptyLines: true });
            const rows = csvData.data as any[][];
            let headerIdx = 0;
            for (let i = 0; i < Math.min(10, rows.length); i++) {
              const rowStr = (rows[i] || []).join(' ').toLowerCase();
              const numCols = (rows[i] || []).filter(c => c !== undefined && c !== null && String(c).trim() !== '').length;
              if (numCols > 3 && (rowStr.includes('name') || rowStr.includes('student'))) {
                headerIdx = i;
                break;
              }
            }
            const headers = rows[headerIdx] || [];
            jsonData = rows.slice(headerIdx + 1).map((row) => {
              const obj: any = {};
              headers.forEach((h: string, idx: number) => {
                if (h) obj[h] = row[idx];
              });
              return obj;
            });
          } else {
            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheet = workbook.SheetNames[0];
            const rows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheet], { header: 1 }) as any[][];
            let headerIdx = 0;
            for (let i = 0; i < Math.min(10, rows.length); i++) {
              const rowStr = (rows[i] || []).join(' ').toLowerCase();
              const numCols = (rows[i] || []).filter((c: any) => c !== undefined && c !== null && String(c).trim() !== '').length;
              if (numCols > 3 && (rowStr.includes('name') || rowStr.includes('student'))) {
                headerIdx = i;
                break;
              }
            }
            const headers = rows[headerIdx] || [];
            jsonData = rows.slice(headerIdx + 1).map((row) => {
              const obj: any = {};
              headers.forEach((h: string, idx: number) => {
                if (h) obj[h] = row[idx];
              });
              return obj;
            });
          }

          // Filter out rows that are completely empty or filled with "empty!"
          const validJsonData = jsonData.filter(row => {
            return Object.values(row).some(v => v !== undefined && v !== null && String(v).trim() !== '' && String(v).trim().toLowerCase() !== 'empty!');
          });

          const parsedLeads: GroupedFiles[] = validJsonData.map((row: any, i: number) => {
             const cleanVal = (v: any) => typeof v === 'string' && v.trim().toLowerCase() === 'empty!' ? '' : v;

             // Improved Key Detection combining exact/fuzzy matches
             const nameKey = Object.keys(row).find(k => k.toLowerCase().includes('student') || k.toLowerCase() === 'name') || '';
             
             // Prioritize Father, then Parent, then Mother
             const fatherKey = Object.keys(row).find(k => k.toLowerCase().includes('father'));
             const motherKey = Object.keys(row).find(k => k.toLowerCase().includes('mother'));
             const generalParentKey = Object.keys(row).find(k => k.toLowerCase().includes('parent') || k.toLowerCase().includes('guardian'));
             const parentKey = fatherKey || generalParentKey || motherKey || '';
             
             const phoneKey = Object.keys(row).find(k => k.toLowerCase().includes('phone') || k.toLowerCase().includes('mobile')) || '';
             const emailKey = Object.keys(row).find(k => k.toLowerCase().includes('email')) || '';
             const classKey = Object.keys(row).find(k => k.toLowerCase().includes('class') || k.toLowerCase().includes('grade')) || '';
             const dobKey = Object.keys(row).find(k => k.toLowerCase().replace(/\s/g, '').includes('dob') || k.toLowerCase().replace(/\s/g, '').includes('dateofbirth')) || '';
             const genderKey = Object.keys(row).find(k => k.toLowerCase().includes('gender') || k.toLowerCase().includes('sex')) || '';
             const previousSchoolKey = Object.keys(row).find(k => k.toLowerCase().includes('school') || k.toLowerCase().includes('previous')) || '';
             const aadhaarKey = Object.keys(row).find(k => k.toLowerCase().includes('aadhaar')) || '';
             const rollKey = Object.keys(row).find(k => k.toLowerCase().includes('roll')) || '';
             const admissionDateKey = Object.keys(row).find(k => k.toLowerCase().includes('admission date')) || '';
             const altContactKey = Object.keys(row).find(k => k.toLowerCase().includes('alternate contact') || k.toLowerCase().includes('alt')) || '';

             const studentName = cleanVal(row[nameKey]) || `Bulk Student ${i+1}`;
             let rawPhone = String(cleanVal(row[phoneKey]) || '').replace(/[^0-9]/g, '');
             if (!rawPhone || rawPhone.length < 10) rawPhone = '0000000000';
             
             return {
                studentKey: studentName,
                formFile: null,
                certificateFile: null,
                status: 'ready',
                extractedData: {
                  studentName: studentName,
                  dateOfBirth: dobKey ? (cleanVal(row[dobKey]) || null) : null,
                  gender: genderKey ? (cleanVal(row[genderKey]) || null) : null,
                  classApplyingFor: cleanVal(row[classKey]) || 'Nursery',
                  previousSchool: previousSchoolKey ? (cleanVal(row[previousSchoolKey]) || null) : null,
                  fatherName: fatherKey ? cleanVal(row[fatherKey]) : (parentKey ? cleanVal(row[parentKey]) : null),
                  motherName: motherKey ? cleanVal(row[motherKey]) : null,
                  primaryPhone: rawPhone,
                  email: cleanVal(row[emailKey]) || '',
                  address: cleanVal(row.Address) || null,
                  notes: `Aadhaar: ${cleanVal(row[aadhaarKey]) || 'N/A'}\nAdmission Date: ${cleanVal(row[admissionDateKey]) || 'N/A'}\nRoll: ${cleanVal(row[rollKey]) || 'N/A'}\nAlt Contact: ${cleanVal(row[altContactKey]) || 'N/A'}`,
                  isNameCorrected: false,
                  isDobCorrected: false,
                  confidence: 'high' as const
                }
             };
          });
          
          resolve(parsedLeads);
        } catch (err) {
           console.error("Tabular parse error", err);
           resolve([]);
        }
      };
      
      if (file.name.toLowerCase().endsWith('.csv')) {
        reader.readAsText(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    });
  };

  const startProcessing = async () => {
    if (fileGroups.length === 0) return;
    setIsProcessing(true);
    setError(null);

    const updatedGroups = [...fileGroups];

    for (let i = 0; i < updatedGroups.length; i++) {
      if (updatedGroups[i].status !== 'pending') continue;

      updatedGroups[i].status = 'scanning';
      setFileGroups([...updatedGroups]);

      try {
        const { formFile, certificateFile } = updatedGroups[i];
        
        // We prioritize the form file for OCR, but Gemini can handle both
        const mainFile = formFile || certificateFile;
        if (!mainFile) throw new Error('No valid file found');

        const data = await extractAdmissionFormData(mainFile, certificateFile || undefined);
        
        updatedGroups[i].extractedData = data;
        updatedGroups[i].status = 'ready';
      } catch (err: any) {
        updatedGroups[i].status = 'error';
        updatedGroups[i].error = err.message || 'AI Scan Failed';
      }

      setProgress(Math.round(((i + 1) / updatedGroups.length) * 100));
      setFileGroups([...updatedGroups]);
    }

    setIsProcessing(false);
  };

  const handleFinalImport = async () => {
    const readyGroups = fileGroups.filter(g => g.status === 'ready');
    if (readyGroups.length === 0) return;

    setIsProcessing(true);
    setError(null);
    const user = auth.currentUser;
    if (!user) {
      setError('You must be logged in to perform this action');
      setIsProcessing(false);
      return;
    }

    const token = await user.getIdToken();
    const results = [...fileGroups];

    // 1. Separate spreadsheet data from image forms
    const spreadsheetLeads = results.filter(g => g.status === 'ready' && !g.formFile && !g.certificateFile);
    const imageLeads = results.filter(g => g.status === 'ready' && (g.formFile || g.certificateFile));

    // 2. Handle Spreadsheet leads in BULK (much faster, one request)
    if (spreadsheetLeads.length > 0) {
      try {
        const bulkPayload = spreadsheetLeads.map(g => ({
          parentName: g.extractedData?.fatherName || g.extractedData?.motherName || 'Parent',
          parentEmail: g.extractedData?.email || '',
          parentPhone: g.extractedData?.primaryPhone || '',
          studentName: g.extractedData?.studentName || g.studentKey,
          studentGender: g.extractedData?.gender || null,
          studentAge: 0, // Age will be calculated on backend if DOB exists
          dateOfBirth: g.extractedData?.dateOfBirth || null,
          preferredGrade: g.extractedData?.classApplyingFor || 'Nursery',
          currentSchool: g.extractedData?.previousSchool || '',
          notes: g.extractedData?.notes || '',
          branchId,
          inquirySource: 'spreadsheet-bulk-import'
        }));

        const bulkResponse = await axios.post(`${API_URL}/leads/bulk`, { leads: bulkPayload, branchId }, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await getAuthToken()}`
          }
        });

        const bulkResult = bulkResponse.data;
        
        if (bulkResult.success) {
          // Update status of all involved groups
          spreadsheetLeads.forEach(lead => {
            const idx = results.findIndex(r => r.studentKey === lead.studentKey);
            if (idx !== -1) results[idx].status = 'completed';
          });
        } else {
          throw new Error(bulkResult.error || 'Bulk upload failed');
        }
      } catch (err: any) {
        console.error('Bulk Import Error:', err);
        spreadsheetLeads.forEach(lead => {
          const idx = results.findIndex(r => r.studentKey === lead.studentKey);
          if (idx !== -1) {
            results[idx].status = 'error';
            results[idx].error = err.message || 'Bulk Import Failed';
          }
        });
      }
      setFileGroups([...results]);
    }

    // 3. Handle Image leads Individually (need doc uploads)
    for (let i = 0; i < imageLeads.length; i++) {
      const group = imageLeads[i];
      const idx = results.findIndex(r => r.studentKey === group.studentKey);
      if (idx === -1) continue;

      results[idx].status = 'uploading';
      setFileGroups([...results]);

      try {
        if (!group.extractedData) throw new Error('No data available');

        // Lead creation
        const leadResponse = await axios.post(`${API_URL}/leads`, {
            parentName: group.extractedData.fatherName || group.extractedData.motherName || 'Parent',
            parentEmail: group.extractedData.email || '',
            parentPhone: group.extractedData.primaryPhone || '0000000000',
            studentName: group.extractedData.studentName || group.studentKey,
            studentGender: group.extractedData.gender || null,
            dateOfBirth: group.extractedData.dateOfBirth || null,
            preferredGrade: group.extractedData.classApplyingFor || 'Nursery',
            currentSchool: group.extractedData.previousSchool || '',
            notes: group.extractedData.notes || '',
            branchId,
            inquirySource: 'bulk-ai-import'
          }, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${await getAuthToken()}`
            }
          });

        const leadResult = leadResponse.data;
        if (!leadResult.success) throw new Error(leadResult.error || 'Failed to create lead');

        const leadId = leadResult.leadId;

        // Document uploading
        const docUrls: { url: string; type: string }[] = [];
        if (group.formFile) {
          const url = await uploadLeadDocument(group.formFile, branchId, leadId, 'admission_form');
          docUrls.push({ url, type: 'admission_form' });
        }
        if (group.certificateFile) {
          const url = await uploadLeadDocument(group.certificateFile, branchId, leadId, 'birth_certificate');
          docUrls.push({ url, type: 'birth_certificate' });
        }

        // Link documents
        if (docUrls.length > 0) {
          await axios.put(`${API_URL}/leads/${leadId}`, { 
              formImageUrl: docUrls[0].url, 
              documents: docUrls
          }, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${await getAuthToken()}`
            }
          });
        }

        results[idx].status = 'completed';
      } catch (err: any) {
        results[idx].status = 'error';
        results[idx].error = err.message || 'Upload failed';
      }
      setFileGroups([...results]);
    }

    setIsProcessing(false);
    
    // Check if we can auto-close
    const hasSuccess = results.some(g => g.status === 'completed');
    const hasErrors = results.some(g => g.status === 'error');

    if (hasSuccess) {
      onUploadSuccess();
      if (!hasErrors) {
        setTimeout(onClose, 1500);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-center bg-gray-50 rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <SparklesIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Bulk Import System</h2>
              <p className="text-sm text-gray-500">Upload CSV, Excel data arrays, or AI-scan multiple image forms</p>
            </div>
          </div>
          <button onClick={onClose} disabled={isProcessing} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {fileGroups.length === 0 ? (
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-3 border-dashed border-gray-200 rounded-2xl p-12 text-center hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer group"
            >
              <ArrowUpTrayIcon className="h-16 w-16 text-gray-400 mx-auto mb-4 group-hover:text-blue-500 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">Select Student Files</h3>
              <p className="text-gray-500 mb-6">Select CSV/XLSX spreadsheets or multiple image forms (JPEG, PNG).</p>
              <button className="bg-blue-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-blue-700 transition-shadow shadow-md">
                Choose Files
              </button>
              <input 
                ref={fileInputRef}
                type="file" 
                multiple 
                accept="image/*,.csv,.xlsx,.xls"
                className="hidden" 
                onChange={handleFileSelection} 
              />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Progress Bar */}
              {isProcessing && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-blue-700">Processing Queue...</span>
                    <span className="text-sm font-bold text-blue-700">{progress}%</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2.5">
                    <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-500" style={{ width: `${progress}%` }}></div>
                  </div>
                </div>
              )}

              {/* Grid/Table */}
              <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Student / Files</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Extracted Info</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Contact</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {fileGroups.map((group, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4">
                          <div className="flex flex-col gap-1">
                            <span className="font-bold text-gray-900 capitalize text-sm">{group.studentKey}</span>
                            <div className="flex gap-2 text-[10px]">
                              {(!group.formFile && !group.certificateFile) && (
                                <span className="flex items-center gap-1 text-green-600 bg-green-50 px-1.5 py-0.5 rounded border border-green-100">
                                  <DocumentMagnifyingGlassIcon className="h-3 w-3" /> Spreadsheet Row
                                </span>
                              )}
                              {group.formFile && (
                                <span className="flex items-center gap-1 text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100">
                                  <PhotoIcon className="h-3 w-3" /> Form
                                </span>
                              )}
                              {group.certificateFile && (
                                <span className="flex items-center gap-1 text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-100">
                                  <ShieldCheckIcon className="h-3 w-3" /> Certificate
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          {group.extractedData ? (
                            <div className="space-y-1">
                              <div className="flex items-center gap-1 p-0.5">
                                <span className="text-sm font-bold text-gray-900">{group.extractedData.studentName}</span>
                                {group.extractedData.isNameCorrected && (
                                  <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full flex items-center gap-0.5 font-bold whitespace-nowrap" title="Verified by Birth Certificate">
                                    <ShieldCheckIcon className="h-3 w-3" /> VERIFIED
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">
                                {group.extractedData.classApplyingFor} • {group.extractedData.dateOfBirth} 
                                {group.extractedData.isDobCorrected && <span className="ml-1 text-green-600 font-bold" title="DOB taken from Certificate">✓</span>}
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Waiting to scan...</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          {group.extractedData ? (
                            <div className="text-xs space-y-0.5">
                              <div className="font-medium text-gray-700">{group.extractedData.primaryPhone}</div>
                              <div className="text-gray-400 truncate max-w-[150px]">{group.extractedData.email || 'No Email'}</div>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-300">---</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex justify-center">
                            {group.status === 'pending' && <span className="text-xs font-bold text-gray-400 bg-gray-100 px-3 py-1 rounded-full">PENDING</span>}
                            {group.status === 'scanning' && (
                              <div className="flex items-center gap-2 text-blue-600 text-xs font-bold">
                                <div className="animate-spin h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full" />
                                SCANNING
                              </div>
                            )}
                            {group.status === 'ready' && <span className="text-xs font-bold text-green-600 bg-green-50 border border-green-200 px-3 py-1 rounded-full flex items-center gap-1"><CheckCircleIcon className="h-3 w-3" /> READY</span>}
                            {group.status === 'error' && <span className="text-xs font-bold text-red-600 bg-red-50 border border-red-200 px-3 py-1 rounded-full flex items-center gap-1" title={group.error}><ExclamationCircleIcon className="h-3 w-3" /> ERROR</span>}
                            {group.status === 'uploading' && <span className="text-xs font-bold text-orange-600 animate-pulse">UPLOADING...</span>}
                            {group.status === 'completed' && <span className="text-xs font-bold text-blue-600 bg-blue-50 border border-blue-200 px-3 py-1 rounded-full">COMPLETED</span>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
              <ExclamationCircleIcon className="h-5 w-5" />
              <p className="text-sm font-medium">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 rounded-b-xl flex justify-between items-center">
          <button 
            onClick={reset} 
            disabled={isProcessing}
            className="text-sm font-bold text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            Clear All
          </button>
          <div className="flex gap-3">
            <button 
              onClick={onClose} 
              disabled={isProcessing}
              className="px-6 py-2 border rounded-lg text-sm font-bold text-gray-600 hover:bg-white"
            >
              Cancel
            </button>
            {fileGroups.some(g => g.status === 'pending') ? (
              <button 
                onClick={startProcessing}
                disabled={isProcessing}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 shadow-md flex items-center gap-2"
              >
                <DocumentMagnifyingGlassIcon className="h-4 w-4" />
                Start Scanning
              </button>
            ) : (
              <button 
                onClick={handleFinalImport}
                disabled={isProcessing || !fileGroups.some(g => g.status === 'ready')}
                className="px-6 py-2 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 shadow-md flex items-center gap-2"
              >
                <CheckCircleIcon className="h-4 w-4" />
                Import {fileGroups.filter(g => g.status === 'ready').length} Leads
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkImageImportModal;
